import React, { useState } from 'react';
import { 
  FaBolt, 
  FaCheckCircle, 
  FaBookOpen, 
  FaArrowRight, 
  FaFire, 
  FaAward, 
  FaPlus, 
  FaSearch, 
  FaFilter,
  FaRegComment,
  FaShareAlt
} from 'react-icons/fa';

// Mock active learning activities and verified commits in the community network
const INITIAL_PULSE_FEED = [
  {
    id: "pulse_01",
    type: "viral_prompt", // Drives discovery / dynamic sprint generation
    user: {
      name: "Kandarp Dave",
      handle: "kdave",
      avatar: "KD",
      streak: 14
    },
    actionText: "is learning Knex.js",
    targetItem: "Knex.js",
    itemType: "Tool",
    context: "Generating custom 3-week syllabus targeting 'Schema Migrations & Transaction Lock-Safety'.",
    interactable: true
  },
  {
    id: "pulse_02",
    type: "verified_commit",
    user: {
      name: "Elena Rostova",
      handle: "elena_r",
      avatar: "ER",
      streak: 8
    },
    actionText: "successfully verified a new competency block",
    targetItem: "LLM Structured Extraction",
    itemType: "Skill",
    context: "Passed dynamic micro-quiz context: 'Zod/Pydantic validation exceptions & self-correcting prompt iterations'.",
    interactable: false,
    score: "3/3",
    timestamp: "10 minutes ago"
  },
  {
    id: "pulse_03",
    type: "milestone",
    user: {
      name: "Marcus Sterling",
      handle: "msterling",
      avatar: "MS",
      streak: 30
    },
    actionText: "unlocked the 'Arch-Database' credential tier",
    targetItem: "PostgreSQL Depth",
    itemType: "Domain Mastery",
    context: "Successfully logged 50+ verifications spanning index query paths and transaction isolation levels.",
    interactable: false,
    badge: "Mastery Level 4",
    timestamp: "2 hours ago"
  },
  {
    id: "pulse_04",
    type: "viral_prompt",
    user: {
      name: "Sarah Jenkins",
      handle: "sjenkins",
      avatar: "SJ",
      streak: 5
    },
    actionText: "is learning Gemini API structured schemas",
    targetItem: "Gemini API",
    itemType: "Tool",
    context: "Setting up automated validation JSON configurations for structured output extractions.",
    interactable: true
  }
];

export default function SocialActivityPulse({ onGenerateSyllabus }) {
  const [feed, setFeed] = useState(INITIAL_PULSE_FEED);
  const [filterType, setFilterType] = useState('all');

  const handleInteract = (targetItem) => {
    if (onGenerateSyllabus) {
      onGenerateSyllabus(targetItem);
    } else {
      alert(`Triggering automated course generation pipeline for: ${targetItem}`);
    }
  };

  const filteredFeed = feed.filter(item => {
    if (filterType === 'all') return true;
    if (filterType === 'learning') return item.type === 'viral_prompt';
    if (filterType === 'verifications') return item.type === 'verified_commit' || item.type === 'milestone';
    return true;
  });

  return (
    <div style={styles.container}>
      <div style={styles.feedLayout}>
        
        {/* Left Column: Core Stream */}
        <div style={styles.streamMain}>
          
          {/* Feed Filter Header bar */}
          <div style={styles.filterBar}>
            <div style={styles.filterLeft}>
              <button 
                onClick={() => setFilterType('all')}
                style={{ ...styles.filterTab, ...(filterType === 'all' ? styles.activeFilterTab : {}) }}
              >
                All Pulses
              </button>
              <button 
                onClick={() => setFilterType('learning')}
                style={{ ...styles.filterTab, ...(filterType === 'learning' ? styles.activeFilterTab : {}) }}
              >
                Learning Sprints
              </button>
              <button 
                onClick={() => setFilterType('verifications')}
                style={{ ...styles.filterTab, ...(filterType === 'verifications' ? styles.activeFilterTab : {}) }}
              >
                Verifications
              </button>
            </div>
            <div style={styles.filterRight}>
              <FaFilter style={{ color: '#8b949e', marginRight: '6px' }} />
              <span style={{ fontSize: '12px', color: '#8b949e' }}>Signal Isolation On</span>
            </div>
          </div>

          {/* Core Feed List rendering */}
          <div style={styles.feedStreamList}>
            {filteredFeed.map((item) => (
              <div key={item.id} style={styles.feedCard}>
                
                {/* Card Top: Actor profile footprint */}
                <div style={styles.cardHeader}>
                  <div style={styles.actorAvatar}>{item.user.avatar}</div>
                  <div style={styles.actorMeta}>
                    <div style={styles.actorNameRow}>
                      <span style={styles.actorName}>{item.user.name}</span>
                      <span style={styles.actorHandle}>@{item.user.handle}</span>
                      {item.user.streak >= 10 && (
                        <span style={styles.streakBadge}>
                          <FaFire /> {item.user.streak} day streak
                        </span>
                      )}
                    </div>
                    <p style={styles.actorActionText}>
                      {item.actionText}{' '}
                      <strong style={styles.highlightTarget}>{item.targetItem}</strong>
                    </p>
                  </div>
                  {item.timestamp && <span style={styles.timeLabel}>{item.timestamp}</span>}
                </div>

                {/* Card Middle: Detailed contextual proof payload */}
                <div style={styles.cardBody}>
                  <p style={styles.contextText}>{item.context}</p>
                  
                  {/* Dynamic decorations depending on event type */}
                  {item.type === 'verified_commit' && (
                    <div style={styles.verificationIndicatorBox}>
                      <FaCheckCircle style={styles.successIconMin} />
                      <span style={styles.indicatorText}>Quiz Verified Score: {item.score} • Consensus Confirmed</span>
                    </div>
                  )}

                  {item.type === 'milestone' && (
                    <div style={styles.milestoneIndicatorBox}>
                      <FaAward style={styles.awardIconMin} />
                      <span style={styles.indicatorText}>{item.badge} Earned • Public Ledger Index Appended</span>
                    </div>
                  )}
                </div>

                {/* Card Bottom: Interactive prompt panel or social counters */}
                <div style={styles.cardFooter}>
                  {item.interactable ? (
                    <button 
                      onClick={() => handleInteract(item.targetItem)}
                      style={styles.actionPromptBtn}
                    >
                      <FaBookOpen style={{ marginRight: '6px' }} /> Learn {item.targetItem}? (Generate Sprint Syllabus)
                    </button>
                  ) : (
                    <div style={styles.socialActionsRow}>
                      <button style={styles.socialBtn} onClick={() => alert("Verification audit link shared.")}>
                        <FaShareAlt /> Share Verification Code
                      </button>
                      <button style={styles.socialBtn} onClick={() => alert("Comments functionality coming soon.")}>
                        <FaRegComment /> 2 replies
                      </button>
                    </div>
                  )}
                </div>

              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Trending Topics / Dynamic learning prompts */}
        <aside style={styles.trendsSidebar}>
          <div style={styles.sidebarSection}>
            <h4 style={styles.sidebarTitle}><FaBolt style={{ color: '#d29922' }} /> Trending Sprint Topics</h4>
            <p style={styles.sidebarDesc}>Highly generated learning pathways based on active hiring gaps:</p>
            
            <div style={styles.trendingList}>
              <div style={styles.trendItem} onClick={() => handleInteract("PostgreSQL")}>
                <div style={styles.trendTextRow}>
                  <span style={styles.trendIndex}>1.</span>
                  <span style={styles.trendSkillName}>PostgreSQL Optimizer Analysis</span>
                </div>
                <span style={styles.trendVolume}>42 active builders</span>
              </div>
              <div style={styles.trendItem} onClick={() => handleInteract("Gemini API")}>
                <div style={styles.trendTextRow}>
                  <span style={styles.trendIndex}>2.</span>
                  <span style={styles.trendSkillName}>Structured API JSON extraction</span>
                </div>
                <span style={styles.trendVolume}>29 active builders</span>
              </div>
              <div style={styles.trendItem} onClick={() => handleInteract("AWS Lambda")}>
                <div style={styles.trendTextRow}>
                  <span style={styles.trendIndex}>3.</span>
                  <span style={styles.trendSkillName}>Serverless Worker Orchestration</span>
                </div>
                <span style={styles.trendVolume}>18 active builders</span>
              </div>
            </div>
          </div>
        </aside>

      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0d1117',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  feedLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 300px',
    gap: '24px',
    maxWidth: '1240px',
    margin: '0 auto'
  },
  streamMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  filterBar: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '8px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  filterLeft: {
    display: 'flex',
    gap: '12px'
  },
  filterTab: {
    background: 'none',
    border: 'none',
    color: '#8b949e',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.15s'
  },
  activeFilterTab: {
    backgroundColor: '#21262d',
    color: '#58a6ff',
    fontWeight: '600'
  },
  filterRight: {
    display: 'flex',
    alignItems: 'center'
  },
  feedStreamList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  feedCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  cardHeader: {
    display: 'flex',
    gap: '12px',
    position: 'relative',
    alignItems: 'flex-start'
  },
  actorAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#30363d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#f0f6fc',
    flexShrink: 0
  },
  actorMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  actorNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  actorName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc'
  },
  actorHandle: {
    fontSize: '12px',
    color: '#8b949e'
  },
  streakBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    color: '#ff6b6b',
    border: '1px solid rgba(255, 107, 107, 0.15)',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '600'
  },
  actorActionText: {
    margin: 0,
    fontSize: '13px',
    color: '#8b949e'
  },
  highlightTarget: {
    color: '#58a6ff',
    fontWeight: '600'
  },
  timeLabel: {
    position: 'absolute',
    right: 0,
    top: 2,
    fontSize: '11px',
    color: '#8b949e'
  },
  cardBody: {
    paddingLeft: '52px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  contextText: {
    margin: 0,
    fontSize: '13px',
    color: '#c9d1d9',
    lineHeight: '1.5'
  },
  verificationIndicatorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(46, 204, 113, 0.05)',
    border: '1px solid rgba(46, 204, 113, 0.1)',
    borderRadius: '4px',
    padding: '8px 12px'
  },
  milestoneIndicatorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(210, 153, 34, 0.05)',
    border: '1px solid rgba(210, 153, 34, 0.1)',
    borderRadius: '4px',
    padding: '8px 12px'
  },
  successIconMin: {
    color: '#2ecc71',
    fontSize: '13px',
    flexShrink: 0
  },
  awardIconMin: {
    color: '#d29922',
    fontSize: '13px',
    flexShrink: 0
  },
  indicatorText: {
    fontSize: '12px',
    color: '#c9d1d9'
  },
  cardFooter: {
    paddingLeft: '52px',
    borderTop: '1px solid #21262d',
    paddingTop: '12px',
    marginTop: '2px'
  },
  actionPromptBtn: {
    backgroundColor: '#238636',
    border: '1px solid #2ea44f',
    borderRadius: '6px',
    color: '#ffffff',
    padding: '8px 16px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.15s'
  },
  socialActionsRow: {
    display: 'flex',
    gap: '16px'
  },
  socialBtn: {
    background: 'none',
    border: 'none',
    color: '#8b949e',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: 0,
    transition: 'color 0.15s'
  },
  trendsSidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  sidebarSection: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '20px'
  },
  sidebarTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: '0 0 8px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  sidebarDesc: {
    margin: '0 0 16px 0',
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: '1.4'
  },
  trendingList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  trendItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    backgroundColor: '#0d1117',
    border: '1px solid #21262d',
    transition: 'border-color 0.15s'
  },
  trendTextRow: {
    display: 'flex',
    gap: '6px'
  },
  trendIndex: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#8b949e'
  },
  trendSkillName: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#c9d1d9'
  },
  trendVolume: {
    fontSize: '11px',
    color: '#8b949e',
    paddingLeft: '16px'
  }
};